import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { JobClassifierCategory } from '@freelancer/datastore/collections';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export enum JobType {
  PROJECT = 'project',
  CONTEST = 'contest',
  LOCAL = 'local',
}

@Component({
  selector: 'app-job-type',
  template: `
    <fl-heading
      i18n="Post Job Page job type field label"
      [headingType]="HeadingType.H3"
      [flMarginBottom]="Margin.XXXSMALL"
      [size]="TextSize.MID"
    >
      How would you like to get it done?
    </fl-heading>
    <fl-bit [flMarginBottom]="Margin.SMALL">
      <fl-banner-alert
        i18n="Post Job Page job type project recommendation banner"
        *ngIf="jobClassification === JobClassifierCategory.LIKELY_PROJECT"
        [type]="BannerAlertType.INFO"
        [flMarginBottom]="Margin.XXSMALL"
      >
        Based on the description you have written, we recommend posting a
        project. Work with a freelancer 1-on-1 and only pay them when you're
        happy with the completed work.
      </fl-banner-alert>
      <fl-banner-alert
        i18n="Post Job Page job type contest recommendation banner"
        *ngIf="jobClassification === JobClassifierCategory.LIKELY_CONTEST"
        [type]="BannerAlertType.INFO"
        [flMarginBottom]="Margin.XXSMALL"
      >
        Based on the description you have written, we recommend posting a
        contest. Contests are great for when you want to crowdsource the best
        ideas.
      </fl-banner-alert>
    </fl-bit>
    <fl-bit [flMarginBottom]="Margin.XLARGE">
      <fl-grid [vAlign]="VerticalAlignment.VERTICAL_STRETCH">
        <fl-col
          [col]="12"
          [colTablet]="6"
          [flMarginBottom]="Margin.SMALL"
          [flMarginBottomTablet]="Margin.NONE"
        >
          <fl-radio-card
            flTrackingLabel="Project"
            i18n-pictureAlt="Project type icon alt text"
            pictureAlt="Project type icon"
            pictureSrc="job-post/project-icon.svg"
            [control]="control"
            [isSelected]="isProjectCardSelected$ | async"
            [value]="JobType.PROJECT"
            (click)="cardSelected.emit(JobType.PROJECT)"
          >
            <fl-radio-card-body>
              <fl-ribbon
                i18n="
                   Post Job Page job type - project option recommended ribbon
                "
                *ngIf="
                  jobClassification === JobClassifierCategory.LIKELY_PROJECT
                "
              >
                Recommended
              </fl-ribbon>
              <fl-ribbon
                i18n="
                   Post Job Page job type - project option most popular ribbon
                "
                *ngIf="jobClassification === JobClassifierCategory.UNKNOWN"
              >
                Most Popular
              </fl-ribbon>
              <fl-text
                i18n="Post Job Page job type - project option title"
                [size]="TextSize.SMALL"
                [weight]="FontWeight.BOLD"
              >
                Post a project
              </fl-text>

              <fl-text
                i18n="Post Job Page job type - project option description"
              >
                Receive free quotes, best for when you have a specific idea, the
                project is not visual in nature or the project is complex.
              </fl-text>
            </fl-radio-card-body>
          </fl-radio-card>
        </fl-col>
        <fl-col [col]="12" [colTablet]="6">
          <fl-radio-card
            flTrackingLabel="Contest"
            i18n-pictureAlt="Contest type icon alt text"
            pictureAlt="Contest type icon"
            pictureSrc="job-post/contest-icon.svg"
            [control]="control"
            [isSelected]="isContestCardSelected$ | async"
            [value]="JobType.CONTEST"
            (click)="cardSelected.emit(JobType.CONTEST)"
          >
            <fl-radio-card-body>
              <fl-ribbon
                i18n="
                   Post Job Page job type - contest option recommended ribbon
                "
                *ngIf="
                  jobClassification === JobClassifierCategory.LIKELY_CONTEST
                "
              >
                Recommended
              </fl-ribbon>
              <fl-text
                i18n="Post Job Page job type - contest option title"
                [size]="TextSize.SMALL"
                [weight]="FontWeight.BOLD"
              >
                Start a contest
              </fl-text>
              <fl-text
                i18n="Post Job Page job type - contest option description"
              >
                Crowdsource ideas. Post a prize and get competing entries which
                you can iterate on with feedback. Great for visual designs.
              </fl-text>
            </fl-radio-card-body>
          </fl-radio-card>
        </fl-col>
      </fl-grid>
    </fl-bit>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobTypeComponent implements OnInit {
  BannerAlertType = BannerAlertType;
  TextSize = TextSize;
  FontWeight = FontWeight;
  HeadingType = HeadingType;
  JobType = JobType;
  Margin = Margin;
  JobClassifierCategory = JobClassifierCategory;
  VerticalAlignment = VerticalAlignment;

  @Input() control: FormControl;
  @Output() cardSelected: EventEmitter<JobType> = new EventEmitter();
  @Input() jobClassification: JobClassifierCategory;

  isContestCardSelected$: Rx.Observable<boolean>;
  isProjectCardSelected$: Rx.Observable<boolean>;

  ngOnInit() {
    this.isContestCardSelected$ = this.control.valueChanges.pipe(
      startWith(this.control.value),
      map(jobType => jobType === JobType.CONTEST),
    );

    this.isProjectCardSelected$ = this.control.valueChanges.pipe(
      startWith(this.control.value),
      map(jobType => jobType === JobType.PROJECT),
    );
  }
}
